import React, {useEffect, useRef, useState} from "react";
import {Link} from 'react-router-dom';
import {AddNew} from './AddNew.component';
import {DOCUMENT_TYPE} from "utils/constants";
import * as _ from "lodash";
import {getDateFormatFromLanguage} from "utils/dateFormatter";
import {connect, useSelector} from "react-redux";
import * as reduxSelectors from "../store/application.reducers";
import moment from "moment";
import {Tooltip} from "../tooltip/Tooltip";
import UserProfileTooltip from "../tooltip/UserProfileTooltip.component";
import WorkflowsActionsDropdownComponent from "components/workflows/WorkflowsActionsDropdown.component";
import ActivateWorkflowPopup from "components/popup/ActivateWorkflowPopup.component";
import { Switch } from "components/common/Switch.component";
import {Tag, TagType} from "components/common/Tag.component";
import { ButtonType } from "components/common/Button.component";
import { EllipsisIcon } from "lucide-react";
import { DropdownPosition } from "types/gaiaComponents/dropdownButton.types";
import { ICON_SIZE } from "components/common/constants";
import { hasInvalidApprovers } from "utils/invalidApprovers.function";
import { SHARED_TOOLTIP_ID } from "components/tooltip/AppTooltip.component";

export function Rules(props) {
    const docType = DOCUMENT_TYPE.asString(parseInt(props.docType, 10));
    const translatedDocType = props.translate("documentType." + docType).toLowerCase();
    const highlightRuleRef = React.useRef(null);
    const companyId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const [isAddNewInputVisible, setIsAddNewInputVisible] = useState(false);

    const scrollToElement = () => {
        const {current} = highlightRuleRef;
        // @ts-ignore
        current && current.scrollIntoView({behavior: "smooth"});
    };

    useEffect(scrollToElement, [props.lastCreatedWorkflow]);

    const generateNewRuleNameWithNumber = function () {
        let increment = 1;
        let name = props.translate("workflows.newWorkflow", translatedDocType);
        props.rules.forEach(rule => {
            if (rule.name.indexOf(name) !== -1) {
                let currentIncrement = rule.name.substr(name.length + 1, 1);
                increment = parseInt(currentIncrement, 10) + 1;
                if (isNaN(increment))
                    increment = 1;
            }
        });
        return name + " " + increment;
    };

    const generateNewRuleText = function () {
        if (props.rules.length === 0)
            return props.translate("workflows.createFirstRule", translatedDocType);
        else
            return props.translate("workflows.newWorkflow", translatedDocType);
    };

    const getWorkflowMessage = function (isActive) {
        //has no workflows
        if (props.rules.length === 0)
            return (
                <span className="float-end">
                    <Tag type={TagType.UTILITY_ROSE} message={props.translate("workflows.noWorkflows")} />
                </span>
            );

        //has no active workflows
        if (!isActive)
            return (
                <span className="float-end">
                    <Tag type={TagType.UTILITY_ROSE} message={props.translate("workflows.noActiveWorkflow")} />
                </span>
    );

        return (
            <span className="float-end">
                <Tag type={TagType.UTILITY_GREEN} message={props.translate("workflows.activeWorkflows")} />
            </span>
        );
    };

    const getWorkflowStatus = function () {
        let hasActive = _.findIndex(props.rules, rule => {
            return rule.active === true;
        }) !== -1;
        return hasActive;
    };

    const getActiveWorkflowName = function () {
        return _.find(props.rules, rule => {
            return rule.active === true;
        })?.name;
    };

    const isRuleHighlighted = function (rule) {
        return props.lastCreatedWorkflow &&
            props.lastCreatedWorkflow.id === rule.idRuleConfiguration &&
            ((new Date().getTime()) - props.lastCreatedWorkflow.created < 5000);
    };

    const buildRulesList = function () {
        return props.rules.map((rule) => {
            return (
                <Rule key={rule.idRuleConfiguration}
                      rule={rule}
                      translate={props.translate}
                      activateRule={props.activateRule}
                      activateReadonlyRule={props.activateReadonlyRule}
                      deactivateReadonlyRule={props.deactivateReadonlyRule}
                      activeRuleName={getActiveWorkflowName()}
                      showActions={props.showActions}
                      docType={props.docType}
                      isHighlighted={isRuleHighlighted(rule)}
                      highlightRef={isRuleHighlighted(rule) ? highlightRuleRef : null}/>
            );
        });
    };

    let hasActiveWorkflow = getWorkflowStatus();

    const getMainPanelClasses = () => {
        if (props.rules.length === 0) {
            return isAddNewInputVisible
                ? 'height-0 min-height-0 border-none'
                : 'd-flex align-items-center justify-content-center';
        }
        return isAddNewInputVisible ? 'border-bottom-none radius-bottom-0' : '';
    };

    return (
        <div className="company mb-4" key={props.index}>
            <div className="setting_collapsable-main bg-white radius-4">
                <div className="p-16 d-flex justify-content-between align-items-center">
                    <h3 className="mb-0"><strong>{props.translate("documentTypes." + docType)}</strong></h3>
                    {getWorkflowMessage(hasActiveWorkflow)}
                </div>
                <section className="px-4 actions-list pb-4">
                    <div className={`main-panel radius-4 ${getMainPanelClasses()}`}>
                        {props.rules.length > 0 ?
                            buildRulesList()
                            :
                            !isAddNewInputVisible && (
                                <h3 className="w-100 text-center m-0">
                                    {props.translate("workflows.noMatches")}
                                </h3>
                            )
                        }
                    </div>
                    {props.showActions &&
                        <AddNew
                            defaultValue={generateNewRuleNameWithNumber()}
                            addNewText={generateNewRuleText()}
                            addText={props.translate("workflows.add")}
                            isFirst={props.rules.length === 0}
                            onInputVisibilityChange={setIsAddNewInputVisible}
                            onBlur={(name) => {
                                props.addRule(companyId, name, props.docType);
                            }}/>
                    }
                </section>
            </div>
        </div>
    );
}


const Rule = (props) => {
    let {rule, activateRule, activateReadonlyRule, deactivateReadonlyRule, showActions} = props;
    const dateValueFormatter = getDateFormatFromLanguage(props.userLanguageCode);
    const activateUserInputRef = useRef(null);
    const [showActivate, setShowActivate] = useState(false);
    const preventAction = useSelector(reduxSelectors.getWorkflowsPreventAction);
    const companyId = useSelector(reduxSelectors.getUsersCurrentContextId);

    const ruleOnChange = (event) => {
        const shouldActivate = !rule.active;
        if (rule.readonly) {
            shouldActivate ? activateReadonlyRule(rule, activateRuleErrorCallback, companyId) : deactivateReadonlyRule(rule, activateRuleErrorCallback, companyId);
        } else if (props.showActions) {
            activateRule(rule, shouldActivate, activateRuleErrorCallback, companyId);
        }
        setShowActivate(false);
    };

    const activateRuleErrorCallback = () => {
        rule.active = !rule.active;
    };

    return (
        <div className={`workflow px-3 py-2 row align-items-center`}
             ref={props.highlightRef}>
            {showActivate && <ActivateWorkflowPopup onActivate={ruleOnChange}
                                                    onCancel={() => setShowActivate(false)}
                                                    activate={!rule?.active}
                                                    ruleName={rule?.name}
                                                    replaceActiveRuleName={props.activeRuleName}
                                                    documentType={rule?.documenttype}
                                                    activateUserInputRef={activateUserInputRef}
            ></ActivateWorkflowPopup>}
            <div className="col-md-7 d-flex align-items-center px-0">
                <Switch isChecked={rule.active}
                        onChange={(e) => {
                               if (preventAction) {
                                    e.preventDefault();
                                    e.stopPropagation();
                               } else {
                                   setShowActivate(true);
                               }}}
                        isDisabled={!props.showActions}
                />
                <div className="d-flex flex-column ms-3">
                    <Link to={"/configuration/workflows/workflow/" + rule.idRuleConfiguration}
                          className="text-body text-decoration-none">
                        {rule.name}
                    </Link>
                    {rule.lastUpdateDate && rule.lastUpdateUserDTO &&
                        <div className="font-12 text-body-secondary">
                            {props.translate("workflows.workflowLastUpdateBy", moment(rule.lastUpdateDate).format(dateValueFormatter))}
                            <span
                                  data-for={SHARED_TOOLTIP_ID} data-tip={rule.idRuleConfiguration + "-" + rule.lastUpdateUserDTO.idUser}>
                                {rule.lastUpdateUserDTO ? rule.lastUpdateUserDTO.fullName : ""}
                            </span>
                            <Tooltip
                                id={rule.idRuleConfiguration + "-" + rule.lastUpdateUserDTO.idUser}
                                content={() => <UserProfileTooltip user={rule.lastUpdateUserDTO}/>}
                            />
                        </div>
                    }
                    {!rule.lastUpdateDate &&
                    <div className="font-12 text-body-secondary">
                        {props.translate("workflows.workflowCreatedJustNow")}
                    </div>
                }
                </div>
            </div>

            <div className="col-md-3 d-flex align-items-center justify-content-end px-0">
                {hasInvalidApprovers(rule) && <Tag type={TagType.WARNING} message={props.translate("workflows.invalidApprovers")} />}
            </div>

                <div className="col-md-2 d-flex align-items-center justify-content-end px-0 gap-8">
                    {props.isHighlighted &&
                        <Tag type={TagType.INFORMATION} message={props.translate("workflows.new")} />
                    }
                    {
                        showActions &&
                        <WorkflowsActionsDropdownComponent
                            rule={rule}
                            iconOnly={true}
                            icon={<EllipsisIcon size={ICON_SIZE.MEDIUM}/>}
                            type={ButtonType.GHOST}
                            position={DropdownPosition.BOTTOM_RIGHT}>
                        </WorkflowsActionsDropdownComponent>
                    }
                </div>
        </div>
    );
};
const mapStateToProps = function (store) {
    return {
        userLanguageCode: reduxSelectors.getUsersCurrentLanguage(store),
        currentContextId: reduxSelectors.getUsersCurrentContextId(store),
    };
};

connect(mapStateToProps, {})(Rule);
